import { Link, useLocation } from 'react-router-dom';
import { useAuth, useBranding } from '@/App';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/getting-started', label: 'Docs' },
  { to: '/status', label: 'Status' },
];

export default function PublicNav() {
  const { user } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav data-testid="public-nav" className="fixed top-0 inset-x-0 z-50 border-b border-slate-200/60" style={{background: branding.header_bg_color || 'rgba(255,255,255,0.8)', backdropFilter: branding.header_bg_color ? 'none' : 'blur(16px)'}}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img src={branding.logo_url || '/tensornex-logo.png'} alt={branding.brand_name || 'Tensornex'} className="w-9 h-9 object-contain" />
          <span className="text-xl font-bold tracking-tight text-slate-900" style={{fontFamily: "'Outfit', sans-serif"}}>
            {branding.brand_name || 'Tensornex'}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map(link => {
            const active = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active ? 'brand-text brand-bg-muted' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Link
              to="/dashboard"
              data-testid="nav-dashboard"
              className="brand-btn inline-flex items-center gap-1.5 px-5 py-2 text-sm"
            >
              Dashboard <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <>
              <Link to="/login" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Sign In
              </Link>
              <Link
                to="/signup"
                data-testid="nav-get-started"
                className="brand-btn inline-flex items-center gap-1.5 px-5 py-2 text-sm"
              >
                Get Started <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden text-slate-700" onClick={() => setOpen(!open)}>
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-100 px-5 py-4 space-y-1">
          {navLinks.map(link => (
            <Link key={link.to} to={link.to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
              {link.label}
            </Link>
          ))}
          <div className="pt-3 flex gap-2">
            <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700">Sign In</Link>
            <Link to="/signup" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 brand-btn text-sm">Get Started</Link>
          </div>
        </div>
      )}
    </nav>
  );
}

export function PublicFooter({ footer, branding: brandingProp }) {
  const f = footer || {};
  const ctxBranding = useBranding();
  const b = brandingProp || ctxBranding || {};
  return (
    <footer data-testid="public-footer" className="border-t border-slate-200 bg-slate-50 py-10 px-5">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-2.5">
          <img src={b.logo_url || '/tensornex-logo.png'} alt={f.company_name || 'Tensornex'} className="w-7 h-7 object-contain" />
          <div>
            <p className="text-sm font-bold text-slate-900" style={{fontFamily: "'Outfit', sans-serif"}}>{f.company_name || b.brand_name || 'Tensornex'}</p>
            <p className="text-xs text-slate-500">{f.tagline || ''}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          {(f.links || []).map((link, i) => (
            <a key={i} href={link.url} className="text-xs text-slate-500 hover:text-slate-700 transition-colors">{link.label}</a>
          ))}
        </div>
        <p className="text-xs text-slate-400">All rights reserved.</p>
      </div>
    </footer>
  );
}
